/**
 * Simulation scoring engine — pure functions, no I/O, no React.
 *
 * Everything here is deterministic: same simulation + same commits => same result.
 * That is deliberate. In phase 3 this logic is ported to the backend so the server
 * is authoritative; keeping it pure and side-effect free is what makes that port
 * mechanical rather than a rewrite.
 *
 * NOTE ON SHARING: CRA 5's ModuleScopePlugin forbids importing from outside src/,
 * and this project is not ejected and has no craco. So this cannot literally be a
 * shared module with the backend — the port in phase 3 is a copy, and the round-trip
 * test fixtures are what keep the two honest.
 *
 * SIGN CONVENTION (verbatim from the trainer's spreadsheet):
 *   a positive delta means MORE OF THE THING NAMED, not "better".
 *   +5 downtime is 5 more minutes of downtime, which is bad.
 *   Whether more is good is declared per KPI in `kpi.direction`.
 */

export const KPI_KEYS = ['downtime', 'defect', 'inventory', 'cost', 'custSat'];

const zeroVector = () =>
  KPI_KEYS.reduce((acc, k) => {
    acc[k] = 0;
    return acc;
  }, {});

export const addVectors = (a, b) =>
  KPI_KEYS.reduce((acc, k) => {
    acc[k] = (a?.[k] || 0) + (b?.[k] || 0);
    return acc;
  }, {});

/** Look-ups that tolerate a missing card rather than throwing mid-render. */
export const cardById = (simulation, cardId) =>
  simulation.decisionCards.find((c) => c.id === cardId) || null;

export const eventById = (simulation, eventId) =>
  simulation.eventCards.find((e) => e.id === eventId) || null;

export const deckFor = (simulation, functionKey) =>
  simulation.decisionCards
    .filter((c) => c.functionKey === functionKey)
    .sort((a, b) => a.order - b.order);

export const functionByKey = (simulation, key) =>
  simulation.functions.find((f) => f.key === key) || null;

export const roundByIndex = (simulation, index) =>
  simulation.rounds.find((r) => r.index === index) || null;

/**
 * Resolve one round.
 *
 * @param commits  { [functionKey]: cardId }  — one locked-in card per function
 * @param eventCardId  the wildcard that fires this round, or null
 * @returns per-function attribution plus the round total, so the debrief screen can
 *          show WHICH function moved WHICH number — attribution is the teaching payload.
 */
export const resolveRound = (simulation, commits = {}, eventCardId = null) => {
  const attribution = simulation.functions.map((fn) => {
    const cardId = commits[fn.key];
    const card = cardId ? cardById(simulation, cardId) : null;
    return {
      functionKey: fn.key,
      functionName: fn.name,
      variant: fn.variant,
      card,
      kpi: card ? card.kpi : zeroVector(),
      committed: Boolean(card),
    };
  });

  const event = eventCardId ? eventById(simulation, eventCardId) : null;

  const total = attribution.reduce(
    (acc, a) => addVectors(acc, a.kpi),
    event ? { ...event.kpi } : zeroVector()
  );

  return { attribution, event, total };
};

/**
 * Run every round played so far and return both the per-round results and the
 * running cumulative vector. Round 4 is only meaningful against rounds 1-3, which
 * is the entire reason the format uses multiple rounds.
 */
export const resolveRun = (simulation, commitsByRound = {}, eventsByRound = {}) => {
  const rounds = [];
  let cumulative = zeroVector();

  simulation.rounds.forEach((round) => {
    const commits = commitsByRound[round.index];
    if (!commits) return;
    const eventId =
      eventsByRound[round.index] !== undefined ? eventsByRound[round.index] : round.eventCardId;
    const result = resolveRound(simulation, commits, eventId);
    cumulative = addVectors(cumulative, result.total);
    rounds.push({ index: round.index, title: round.title, ...result, cumulative: { ...cumulative } });
  });

  return { rounds, cumulative };
};

/**
 * Map a raw value onto 0-100 for a gauge, using the simulation's own caps.
 * `normaliseAbs` implements the spreadsheet's literal instruction for Inventory:
 * "use positive; treat shortages as positive absolute value".
 */
export const normaliseKpi = (kpiDef, rawValue) => {
  const v = kpiDef.normaliseAbs ? Math.abs(rawValue) : rawValue;
  if (!kpiDef.cap) return 0;
  return Math.max(0, Math.min(100, Math.round((v / kpiDef.cap) * 100)));
};

/**
 * The same 0-100 figure, plus WHETHER THE CLAMP FIRED.
 * Mirror of the backend copy in backend/src/utils/simulationEngine.js — keep both in step.
 *
 * normaliseKpi saturates silently at both ends, which makes a gauge lie in two ways:
 *   - past the cap every value draws a full bar, so 180 and 900 minutes are identical;
 *   - below zero everything floors at 0, so -21 on a 'higher is better' KPI — the worst
 *     result available — draws the same empty bar as "nothing happened".
 * Both were observed on a real debrief screen.
 *
 * The percentage is unchanged; this only reports what the clamp hid so the UI can mark it.
 */
export const kpiBarState = (kpiDef, rawValue) => {
  const v = kpiDef.normaliseAbs ? Math.abs(rawValue) : rawValue;
  const pct = normaliseKpi(kpiDef, rawValue);
  return {
    pct,
    overCap: Boolean(kpiDef.cap) && v > kpiDef.cap,
    belowFloor: Boolean(kpiDef.cap) && v < 0,
  };
};

/**
 * Is a movement good or bad, given the KPI's declared direction?
 * Returns 'good' | 'bad' | 'neutral' — used only for colour, never for scoring.
 */
export const movementSense = (kpiDef, delta) => {
  if (!delta) return 'neutral';
  if (kpiDef.direction === 'neutral') return 'neutral';
  const better = kpiDef.direction === 'lower' ? delta < 0 : delta > 0;
  return better ? 'good' : 'bad';
};

/**
 * DOMINANCE ADVISORY — authoring aid only.
 *
 * A card dominates when it is at least as good as every alternative on every KPI and
 * strictly better on one. Those cards flatten the exercise, because there is nothing
 * to trade off.
 *
 * IMPORTANT: this is advisory and is shown to the trainer during authoring. It must
 * NEVER auto-repair. Nine of the 27 cards in the reference simulation are good on
 * nothing at all — and those nine are precisely the blame and avoidance cards. A
 * validator that "fixed" them would fabricate upsides for blame-shifting and sand
 * the defensive archetype out of every deck, destroying the behavioural spread the
 * whole exercise depends on.
 */
export const findDominantCards = (simulation, functionKey) => {
  const deck = deckFor(simulation, functionKey);
  const kpiDefs = simulation.kpis;

  const betterOnAxis = (a, b, def) => {
    const av = a.kpi[def.key];
    const bv = b.kpi[def.key];
    if (def.direction === 'neutral') return 0;
    if (av === bv) return 0;
    const aBetter = def.direction === 'lower' ? av < bv : av > bv;
    return aBetter ? 1 : -1;
  };

  return deck
    .map((card) => {
      const dominated = deck.filter((other) => {
        if (other.id === card.id) return false;
        const senses = kpiDefs.map((def) => betterOnAxis(card, other, def));
        return senses.every((s) => s >= 0) && senses.some((s) => s > 0);
      });
      return { card, dominatesCount: dominated.length, dominatesAll: dominated.length === deck.length - 1 };
    })
    .filter((r) => r.dominatesCount > 0)
    .sort((a, b) => b.dominatesCount - a.dominatesCount);
};

/** Cards that are worse-or-equal on every axis — the blame/avoid cards. Expected, not a defect. */
export const findUniformlyPoorCards = (simulation, functionKey) => {
  const deck = deckFor(simulation, functionKey);
  return deck.filter((card) =>
    simulation.kpis.every((def) => {
      if (def.direction === 'neutral') return true;
      const v = card.kpi[def.key];
      return def.direction === 'lower' ? v >= 0 : v <= 0;
    })
  );
};

/**
 * Behavioural archetype mix across everything committed so far.
 * This is what the closing debrief screen is built on — "you collaborated in round 1
 * and blamed each other from round 2 onward" is the sentence the trainer wants.
 */
export const archetypeMix = (simulation, commitsByRound = {}) => {
  const counts = {};
  let total = 0;

  Object.values(commitsByRound).forEach((commits) => {
    Object.values(commits || {}).forEach((cardId) => {
      const card = cardById(simulation, cardId);
      if (!card?.behaviour?.tags) return;
      card.behaviour.tags.forEach((tag) => {
        counts[tag] = (counts[tag] || 0) + 1;
        total += 1;
      });
    });
  });

  return Object.entries(counts)
    .map(([tag, count]) => ({ tag, count, share: total ? Math.round((count / total) * 100) : 0 }))
    .sort((a, b) => b.count - a.count);
};

/** Average cooperativeness per round — the single clearest trend line for the debrief. */
export const cooperationTrend = (simulation, commitsByRound = {}) =>
  simulation.rounds
    .filter((r) => commitsByRound[r.index])
    .map((r) => {
      const commits = commitsByRound[r.index];
      const cards = Object.values(commits)
        .map((id) => cardById(simulation, id))
        .filter(Boolean);
      const avg = cards.length
        ? cards.reduce((s, c) => s + (c.behaviour?.cooperativeness || 0), 0) / cards.length
        : 0;
      return { round: r.index, cooperation: Math.round(avg * 100) };
    });

/**
 * Counterfactual: replay a round with one function's card swapped.
 * Trainer-only. Used live on the debrief screen to answer "what if Maintenance had
 * negotiated instead?" without implying a correct answer existed.
 */
export const counterfactual = (simulation, commits, eventCardId, functionKey, altCardId) => {
  const actual = resolveRound(simulation, commits, eventCardId);
  const alt = resolveRound(simulation, { ...commits, [functionKey]: altCardId }, eventCardId);
  return { actual, alt, delta: KPI_KEYS.reduce((acc, k) => {
    acc[k] = alt.total[k] - actual.total[k];
    return acc;
  }, {}) };
};

/** Format a delta for display with an explicit sign; 0 renders as an em dash. */
export const formatDelta = (value) => {
  if (!value) return '—';
  return value > 0 ? `+${value}` : `${value}`;
};

/**
 * The first storage slot no metric is using yet, or null when all five are taken.
 * A metric outside the five slots can never move (cards only carry those columns), so
 * a new metric must take a real slot — never an invented key like `metric_6`.
 */
export const freeMetricSlot = (kpis = []) =>
  KPI_KEYS.find((slot) => !kpis.some((k) => k.key === slot)) || null;

/* Targets — mirror of the backend helpers in backend/src/utils/simulationEngine.js. */

/** A metric's target as a number, or null when the trainer only declared a direction. */
export const targetOf = (kpiDef) => {
  const t = kpiDef?.target;
  if (t === null || t === undefined || t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Starting value plus everything the rounds moved it by — the number people read. */
export const metricLevel = (kpiDef, change = 0) => (Number(kpiDef?.start) || 0) + (change || 0);

/** 'met' | 'missed', or null with no target. Balance targets are an allowed drift. */
export const targetStatus = (kpiDef, change = 0) => {
  const target = targetOf(kpiDef);
  if (target === null) return null;
  if (kpiDef.direction === 'neutral') return Math.abs(change || 0) <= Math.abs(target) ? 'met' : 'missed';
  const level = metricLevel(kpiDef, change);
  if (kpiDef.direction === 'lower') return level <= target ? 'met' : 'missed';
  return level >= target ? 'met' : 'missed';
};

/** "≤ 30" / "≥ 95" / "within ±10", or '' with no target. */
export const formatTarget = (kpiDef) => {
  const t = targetOf(kpiDef);
  if (t === null) return '';
  if (kpiDef.direction === 'neutral') return `within ±${Math.abs(t)}`;
  return `${kpiDef.direction === 'lower' ? '≤' : '≥'} ${t}`;
};

export default {
  KPI_KEYS,
  addVectors,
  cardById,
  eventById,
  deckFor,
  functionByKey,
  roundByIndex,
  resolveRound,
  resolveRun,
  normaliseKpi,
  kpiBarState,
  movementSense,
  findDominantCards,
  findUniformlyPoorCards,
  archetypeMix,
  cooperationTrend,
  counterfactual,
  formatDelta,
  freeMetricSlot,
  targetOf,
  metricLevel,
  targetStatus,
  formatTarget,
};
